import React, { useState, useEffect, useRef } from 'react';
import { useSettings } from '../context/SettingsContext';
import { enrollSpeaker, getEnrollmentStatus, resetDoctorProfile, listDocuments } from '../services/api';

const ENROLLMENT_SAMPLE_TEXT =
  'Der Patient ist 58 Jahre alt und stellt sich mit seit drei Wochen ' +
  'bestehenden Brustschmerzen vor, die in den linken Arm ausstrahlen. ' +
  'Vorerkrankungen: arterielle Hypertonie seit acht Jahren, Diabetes mellitus Typ 2. ' +
  'Aktuelle Medikation: Metformin 1000 Milligramm zweimal täglich, Ramipril 5 Milligramm morgens. ' +
  'Ich ordne ein EKG, Troponin und ein großes Blutbild an. ' +
  'Bei Verdacht auf akutes Koronarsyndrom erfolgt die sofortige Vorstellung in der Notaufnahme. ' +
  'Der Blutdruck liegt heute bei 155 zu 90, der Puls ist regelmäßig mit 78 Schlägen pro Minute.';

function EnrollmentCard() {
  const [status, setStatus] = useState(null);
  const [phase, setPhase] = useState('idle'); // idle | recording | uploading | done | error
  const [errorMsg, setErrorMsg] = useState('');
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);

  useEffect(() => {
    getEnrollmentStatus().then(setStatus).catch(() => {});
  }, []);

  async function startRecording() {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus' : 'audio/webm';
      const recorder = new MediaRecorder(stream, { mimeType });
      chunksRef.current = [];
      recorder.ondataavailable = e => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      recorder.start();
      mediaRecorderRef.current = recorder;
      setPhase('recording');
      setErrorMsg('');
    } catch {
      setErrorMsg('Mikrofonzugriff verweigert.');
    }
  }

  async function stopAndUpload() {
    const recorder = mediaRecorderRef.current;
    if (!recorder) return;
    setPhase('uploading');
    await new Promise(resolve => {
      recorder.onstop = resolve;
      recorder.stop();
    });
    streamRef.current?.getTracks().forEach(t => t.stop());
    try {
      const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
      const result = await enrollSpeaker(blob);
      setStatus({ enrolled: true, num_segments: result.num_segments });
      setPhase('done');
    } catch (e) {
      setErrorMsg(e.message || 'Fehler beim Upload.');
      setPhase('error');
    }
  }

  async function handleReset() {
    if (!window.confirm('Stimmprofil wirklich löschen?')) return;
    await resetDoctorProfile().catch(() => {});
    setStatus({ enrolled: false, num_segments: 0 });
    setPhase('idle');
  }

  return (
    <div className="card">
      <h2>Stimmprofil</h2>
      <p className="settings-label-desc" style={{ marginBottom: 16 }}>
        Das Stimmprofil ermöglicht die automatische Erkennung Ihrer Stimme in Aufnahmen.
        Nach der Einrichtung wird Ihre Stimme sofort als „Arzt" erkannt — unabhängig davon,
        wie viele andere Personen im Gespräch sind.
      </p>

      <div className="settings-row" style={{ marginBottom: 16 }}>
        <div className="settings-label">
          <span className="settings-label-text">Status</span>
          <span className="settings-label-desc">
            {status === null ? 'Wird geladen…'
              : status.enrolled
                ? `Eingerichtet (${status.num_segments} Segment${status.num_segments !== 1 ? 'e' : ''})`
                : 'Nicht eingerichtet'}
          </span>
        </div>
        {status?.enrolled && (
          <button className="enroll-btn enroll-btn-danger" onClick={handleReset}>
            Profil löschen
          </button>
        )}
      </div>

      {phase === 'idle' || phase === 'error' ? (
        <div>
          <p className="settings-label-desc" style={{ marginBottom: 10 }}>
            Lesen Sie nach dem Start den folgenden Mustertext laut vor — er dauert ca. 20–25 Sekunden
            und enthält typische medizinische Begriffe.
            {status?.enrolled && ' Weitere Aufnahmen verbessern die Genauigkeit.'}
          </p>
          <div className="enroll-sample-text">
            <div className="enroll-sample-label">Mustertext zum Vorlesen</div>
            <p className="enroll-sample-body">{ENROLLMENT_SAMPLE_TEXT}</p>
          </div>
          {errorMsg && <p style={{ color: '#ef4444', fontSize: 13, marginBottom: 8 }}>{errorMsg}</p>}
          <button className="enroll-btn enroll-btn-primary" onClick={startRecording}>
            Aufnahme starten
          </button>
        </div>
      ) : phase === 'recording' ? (
        <div>
          <div className="enroll-sample-text enroll-sample-text--active">
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <span className="enroll-recording-dot" />
              <span style={{ fontSize: 13, fontWeight: 600, color: '#dc2626' }}>Aufnahme läuft — jetzt vorlesen:</span>
            </div>
            <p className="enroll-sample-body">{ENROLLMENT_SAMPLE_TEXT}</p>
          </div>
          <button className="enroll-btn enroll-btn-primary" onClick={stopAndUpload} style={{ marginTop: 10 }}>
            Fertig
          </button>
        </div>
      ) : phase === 'uploading' ? (
        <p style={{ fontSize: 14, color: '#6b7280' }}>Stimmprofil wird erstellt…</p>
      ) : phase === 'done' ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ color: '#22c55e', fontSize: 18 }}>✓</span>
          <span style={{ fontSize: 14, color: '#374151' }}>
            Profil aktualisiert ({status?.num_segments} Segmente).
          </span>
          <button className="enroll-btn enroll-btn-secondary" onClick={() => setPhase('idle')}>
            Weitere Aufnahme
          </button>
        </div>
      ) : null}
    </div>
  );
}

function KnowledgeCard() {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    listDocuments().then(setDocuments).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const totalChunks = documents.reduce((sum, d) => sum + d.chunk_count, 0);

  return (
    <div className="card">
      <h2>Wissensdatenbank</h2>
      <p className="settings-label-desc" style={{ marginBottom: 16 }}>
        Indexierte medizinische Leitlinien und Cochrane Reviews. Die KI verwendet diese
        Inhalte automatisch bei der Diagnoseanalyse.
      </p>

      {loading ? (
        <p className="settings-label-desc">Wird geladen…</p>
      ) : documents.length === 0 ? (
        <p className="settings-label-desc">Noch keine Dokumente indexiert.</p>
      ) : (
        <>
          <div className="settings-row" style={{ marginBottom: 16 }}>
            <div className="settings-label">
              <span className="settings-label-text">{documents.length} Dokumente</span>
              <span className="settings-label-desc">{totalChunks.toLocaleString('de-DE')} indexierte Abschnitte</span>
            </div>
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #e5e7eb', color: '#6b7280' }}>
                <th style={{ textAlign: 'left', padding: '6px 8px' }}>Dateiname</th>
                <th style={{ textAlign: 'left', padding: '6px 8px' }}>Kategorie</th>
                <th style={{ textAlign: 'left', padding: '6px 8px' }}>Gültigkeit</th>
                <th style={{ textAlign: 'right', padding: '6px 8px' }}>Abschnitte</th>
              </tr>
            </thead>
            <tbody>
              {documents.map(doc => (
                <tr key={doc.source_id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                  <td style={{ padding: '6px 8px', maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {doc.filename}
                  </td>
                  <td style={{ padding: '6px 8px', color: '#6b7280' }}>{doc.category}</td>
                  <td style={{ padding: '6px 8px' }}>
                    {doc.valid
                      ? <span style={{ color: '#16a34a', fontWeight: 500 }}>Gültig</span>
                      : <span style={{ color: '#dc2626' }} title={doc.expiry_date ? `Abgelaufen: ${doc.expiry_date}` : ''}>
                          Abgelaufen{doc.expiry_date ? ` (${doc.expiry_date})` : ''}
                        </span>
                    }
                  </td>
                  <td style={{ padding: '6px 8px', textAlign: 'right', color: '#6b7280' }}>{doc.chunk_count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </div>
  );
}

function EinstellungenPage() {
  const { settings, updateSettings } = useSettings();

  return (
    <div className="page">
      <h1 className="page-title">Einstellungen</h1>
      <p className="page-subtitle">Verwalte dein Profil und deine Präferenzen</p>

      <EnrollmentCard />
      <KnowledgeCard />

      <div className="card">
        <h2>KI-Analyse</h2>
        <div className="settings-row">
          <div className="settings-label">
            <span className="settings-label-text">Anzahl Differentialdiagnosen</span>
            <span className="settings-label-desc">
              Wie viele Differentialdiagnosen soll die KI-Analyse vorschlagen?
            </span>
          </div>
          <select
            className="settings-select"
            value={settings.numDiagnoses}
            onChange={(e) => updateSettings({ numDiagnoses: Number(e.target.value) })}
          >
            {[2, 3, 4, 5, 6].map((n) => (
              <option key={n} value={n}>{n} Diagnosen</option>
            ))}
          </select>
        </div>
      </div>

      <div className="card">
        <h2>Bericht</h2>
        <div className="settings-row">
          <div className="settings-label">
            <span className="settings-label-text">Bericht-Template</span>
            <span className="settings-label-desc">
              Wählen Sie das Format für den Abschlussbericht der Session.
            </span>
          </div>
          <select
            className="settings-select"
            value={settings.reportTemplate}
            onChange={(e) => updateSettings({ reportTemplate: e.target.value })}
          >
            <option value="standard">Standard – Vollständiger Bericht</option>
            <option value="kurzbericht">Kurzbericht – Kompakte Übersicht</option>
            <option value="arztbrief">Arztbrief – Formeller Befundbericht</option>
            <option value="stichpunkte">Stichpunkte – Minimale Auflistung</option>
          </select>
        </div>
        <div className="settings-row">
          <div className="settings-label-group">
            <span className="settings-label">Aufbewahrungsdauer Patientendaten</span>
            <span className="settings-description">
              Archivierte Sessions werden nach dieser Zeit automatisch gelöscht.
            </span>
          </div>
          <select
            className="settings-select"
            value={settings.sessionRetentionDays}
            onChange={(e) => updateSettings({ sessionRetentionDays: Number(e.target.value) })}
          >
            <option value={90}>90 Tage</option>
            <option value={180}>180 Tage</option>
            <option value={360}>360 Tage</option>
          </select>
        </div>
        <div className="settings-template-preview">
          {settings.reportTemplate === 'standard' && (
            <p className="settings-template-desc">Vollständiger Bericht mit Anamnese, Ergänzungen und detaillierten Diagnosen inkl. Begründung, Diagnostik, Behandlung und Medikation.</p>
          )}
          {settings.reportTemplate === 'kurzbericht' && (
            <p className="settings-template-desc">Kompakte Zusammenfassung mit den wichtigsten Symptomen und Diagnosen ohne ausführliche Details.</p>
          )}
          {settings.reportTemplate === 'arztbrief' && (
            <p className="settings-template-desc">Formeller Arztbrief-Stil mit Anrede, Fließtext und professionellem Abschluss – geeignet für Überweisungen und Konsilberichte.</p>
          )}
          {settings.reportTemplate === 'stichpunkte' && (
            <p className="settings-template-desc">Minimale stichpunktartige Auflistung aller relevanten Punkte – ideal für schnelle Dokumentation.</p>
          )}
        </div>
      </div>
    </div>
  );
}

export default EinstellungenPage;
